import { type Goal, type GoalTask } from '@fruitful/types';
import { type WorkflowState } from '@mastra/core/workflows';
import { atom } from 'jotai';

import {
    atomWithStorage,
    atomWithWindowStorage,
} from '../common/providers/StorageProvider';
import { StorageKey } from '../common/types/Storage';
import {
    type AdminUser,
    AdminUserRole,
    type Authorization,
    type Guide,
    type GuideDetails,
    type MembershipDetails,
    type MembershipSummary,
} from './entityTypes';

export const emptyAdminUser: AdminUser = {
    id: null,
    stytchMemberId: '',
    memberId: null,
    guideId: null,
    guide: null,
    email: '',
    createdAt: '',
    updatedAt: '',
    role: AdminUserRole.GENERAL,
    authorization: {} satisfies unknown as Authorization,
};
// Initialize the atom with an empty object rather than null/undefined
// This allows us to assert adminUser exists where we are actually trying to
// use it and avoid lots of unnecessary null checks. The app suspends while
// the adminUser.id is null waiting for the value from the server.
export const adminUserAtom = atom<AdminUser>(emptyAdminUser);

export const mutatedAdminUserGuideAtom = atom<Guide | null>(null);

export const isLoadingMembershipsAtom = atom<boolean>(false);
export const allMembershipsAtom = atom<MembershipSummary[]>([]);

export const myMembershipsAtom = atom<MembershipSummary[]>((get) => {
    const allMemberships = get(allMembershipsAtom) ?? [];
    return allMemberships.filter(
        (membership) => membership.guideId === get(adminUserAtom)?.guideId,
    );
});

export const selectedMembershipIdAtom = atomWithWindowStorage<string | null>(
    StorageKey.SELECTED_MEMBERSHIP_ID,
    null,
);

export const selectedMembershipAtom =
    atomWithWindowStorage<MembershipDetails | null>(
        StorageKey.SELECTED_MEMBERSHIP,
        null,
    );

export const mutatedMembershipAtom = atom<MembershipDetails | null>(null);

export const selectedWorkflowRunAtom =
    atomWithWindowStorage<WorkflowState | null>(
        StorageKey.SELECTED_WORKFLOW_RUN,
        null,
    );

export const allGuidesAtom = atom<GuideDetails[]>([]);

export const isEditingGoalsAtom = atom<boolean>(false);

export const mutatedGoalAtom = atom<Goal | null>(null);
export const createdGoalAtom = atom<Goal | null>(null);

export const mutatedGoalTaskAtom = atom<GoalTask | null>(null);
export const createdGoalTaskAtom = atom<GoalTask | null>(null);

export const updatedGoalsAtom = atom<Goal[] | null>(null);
export const updatedGoalTasksAtom = atom<GoalTask[] | null>(null);

export const newGoalToExpandAtom = atom<string | null>(null);
export const allExpandedGoalsAtom = atomWithStorage<Record<string, boolean>>(
    StorageKey.ALL_EXPANDED_GOALS,
    {},
);
